import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { EventDialog } from './event-dialog';
import { ExportInviteDialog } from './export-invite-dialog';
import { UnconfirmedChip, formatRelationship } from './procedural-badges';
import { formatDate, formatDateRange, mapDeltaTypeToCounting } from './procedural-helpers';
import { ProceduralSourceRef } from './procedural-source-drawer';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useDeleteProceduralEvent } from '@/api/mutations/useDeleteProceduralEvent';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { formatTimeZoneLabel } from '@/new/lib/time-zones';
import { useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

interface ProceduralEventRowProps {
  event: ProceduralTimelineEvent;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterName: string;
  onOpenSource: (source: ProceduralSourceRef) => void;
  /** Null = editable. Non-null disables the edit button and is used as the tooltip text. */
  editDisabledReason: string | null;
}

function scrollToEvent(eventId: string) {
  const el = document.getElementById(`procedural-event-${eventId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('ring-2', 'ring-teal-400');
  window.setTimeout(() => el.classList.remove('ring-2', 'ring-teal-400'), 1500);
}

export function ProceduralEventRow({
  event,
  eventsById,
  matterName,
  onOpenSource,
  editDisabledReason,
}: ProceduralEventRowProps) {
  const isEditDisabled = editDisabledReason !== null;
  const { matterId } = useParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteProceduralEvent();
  const openCalculator = useTimelineCalculatorStore((s) => s.open);

  const latestChunk = event.sourceChunks?.[event.sourceChunks.length - 1];
  const sourceFileName = latestChunk?.file?.name ?? null;
  const dependents = Object.values(eventsById).filter((e) => e.anchorEventId === event.id);
  const anchorName = event.anchorEventId ? (eventsById[event.anchorEventId]?.name ?? null) : null;
  const relationship = formatRelationship(event.deltaInterval, event.deltaIntervalType, anchorName);
  const dateRangeText = formatDateRange(event.startDateTime, event.endDateTime);

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!latestChunk?.file) return;
    onOpenSource({
      fileId: latestChunk.file.id,
      fileName: latestChunk.file.name,
      pageNumber: latestChunk.pageNumber,
      chunkId: latestChunk.id,
      content: latestChunk.content,
      boundingBox: latestChunk.boundingBox,
    });
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditOpen(true);
  };

  const handleCalculatorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!matterId) return;
    const anchor = event.anchorEventId ? eventsById[event.anchorEventId] : null;
    const anchorStart = anchor?.startDateTime ?? event.startDateTime;
    openCalculator({
      matterId,
      eventId: event.id,
      eventName: event.name,
      startDate: anchorStart ? new Date(anchorStart) : null,
      days: event.deltaInterval?.days ?? 0,
      counting: mapDeltaTypeToCounting(event.deltaIntervalType, event.jurisdiction),
      timeZone: event.timeZone ?? null,
      endDate: event.endDateTime ? new Date(event.endDateTime) : null,
    });
  };

  const handleConfirmDelete = () => {
    if (!matterId) return;
    deleteEvent(
      { matterId, eventId: event.id },
      {
        onSuccess: () => {
          toast.success('Event deleted.');
          setIsDeleteOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to delete event.');
        },
      },
    );
  };

  return (
    <div
      id={`procedural-event-${event.id}`}
      className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 transition-shadow duration-500"
    >
      <div>
        <div className="flex w-full flex-col gap-2 pr-2 text-left">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="flex min-w-0 items-center gap-2 truncate text-lg text-gray-900">
                    {event.startDateTime !== null && (
                      <>
                        <span className="text-gray-500">{formatDate(event.startDateTime)}</span>
                        <span className="text-sm text-gray-400/60">•</span>
                      </>
                    )}
                    <span className="truncate">{event.name}</span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>{event.name}</TooltipContent>
              </Tooltip>
              {isEditDisabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex cursor-not-allowed">
                      <Button
                        variant="ghost"
                        className="px-1 py-1 text-gray-400 hover:text-gray-800"
                        aria-label="Edit event"
                        disabled
                      >
                        <Icon className="lucide--pencil" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{editDisabledReason}</TooltipContent>
                </Tooltip>
              ) : (
                <Button
                  variant="ghost"
                  className="px-1 py-1 text-gray-400 hover:text-gray-800"
                  onClick={handleEditClick}
                  aria-label="Edit event"
                >
                  <Icon className="lucide--pencil" />
                </Button>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {sourceFileName && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleSourceClick}
                      className="flex max-w-48 items-center gap-1.5 truncate text-xs text-blue-600 hover:underline"
                    >
                      <Icon className="lucide--file-text shrink-0" />
                      <span className="truncate">{sourceFileName}</span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{sourceFileName}</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleCalculatorClick}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Open timeline calculator"
                  >
                    <Icon className="lucide--calculator" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Open timeline calculator</TooltipContent>
              </Tooltip>
              {event.startDateTime !== null ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsExportOpen(true);
                      }}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Send as calendar invite"
                    >
                      <Icon className="lucide--calendar-plus" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Send as calendar invite</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex cursor-not-allowed">
                      <button
                        type="button"
                        disabled
                        className="rounded p-1 text-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Send as calendar invite"
                      >
                        <Icon className="lucide--calendar-plus" />
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Add a date to this event to share it as a calendar invite.</TooltipContent>
                </Tooltip>
              )}
              <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="-mr-1 p-0 text-gray-400 hover:text-gray-700"
                    aria-label="Delete event"
                  >
                    <Icon className="lucide--x" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete event</DialogTitle>
                    <DialogDescription>This action cannot be undone.</DialogDescription>
                  </DialogHeader>
                  <p className="text-sm">
                    Delete <span className="font-medium">{event.name}</span>?
                  </p>
                  {dependents.length > 0 && (
                    <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 px-3 text-xs text-amber-800">
                      <Icon className="lucide--triangle-alert mt-0.5 shrink-0" />
                      <span>
                        {dependents.length === 1
                          ? `“${dependents[0].name}” is calculated based on this event. Deleting will break that relationship.`
                          : `${dependents.length} events are calculated based on this event. Deleting will break those relationships.`}
                      </span>
                    </p>
                  )}
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                      <Icon className={isDeleting ? 'lucide--loader-2 animate-spin' : 'lucide--trash-2'} />
                      Delete event
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {!event.isConfirmed && <UnconfirmedChip />}
            {dateRangeText && (
              <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                <Icon className="lucide--clock text-gray-500" />
                {dateRangeText}
              </Chip>
            )}
            {event.timeZone && (
              <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                <Icon className="lucide--globe text-gray-500" />
                {formatTimeZoneLabel(event.timeZone)}
              </Chip>
            )}
            {relationship && (
              <button
                type="button"
                onClick={() => scrollToEvent(event.anchorEventId!)}
                className="appearance-none focus:outline-none"
                aria-label="Scroll to linked event"
              >
                <Chip className="rounded-lg border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100">
                  <Icon className="lucide--link text-sky-600" />
                  <span className="block max-w-[20rem] truncate">{relationship}</span>
                </Chip>
              </button>
            )}
            {event.createdBy?.email && (
              <Chip className="rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                <Icon className="lucide--user text-gray-500" />
                Created by {event.createdBy.email}
              </Chip>
            )}
          </div>

          {event.description && <p className="text-sm text-gray-600">{event.description}</p>}
        </div>
      </div>

      {matterId && (
        <EventDialog
          event={event}
          eventsById={eventsById}
          matterId={matterId}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}
      <ExportInviteDialog event={event} matterName={matterName} open={isExportOpen} onOpenChange={setIsExportOpen} />
    </div>
  );
}
